<template>
  <v-dialog :value="!!exporter" width="600" @input="close" :persistent="loading">
    <v-card>
      <v-card-title>
        {{ $t('Qlik.Exporter.title') }}
        <v-spacer></v-spacer>
        <v-btn icon outlined @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text v-if="options">
        <v-row>
          <v-col cols="4" v-for="mode in modes" :key="mode.name">
            <v-btn 
              :color="selectedMode === mode.name ? 'primary' : 'grey'" 
              :outlined="selectedMode === mode.name" 
              block 
              text
              @click="selectedMode = mode.name" 
            >
              <v-icon left>{{mode.icon}}</v-icon>
              {{mode.name}}
            </v-btn>
          </v-col>
          <template v-if="selectedMode === 'csv'">
            <v-col cols="6">
              <v-select v-model="options.csv.qFileType" :items="csvTypes" :label="$t('Qlik.Exporter.format')"></v-select>
            </v-col>
            <v-col cols="6">
              <v-select v-model="options.csv.qExportState" :items="csvStates" :label="$t('Qlik.Exporter.data')"></v-select>
            </v-col>
          </template>
          <template v-if="selectedMode === 'image'">
            <v-col cols="6">
              <v-text-field v-model.number="options.image.height" :label="$t('Global.Common.height')" type="number" hint="pixels"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field v-model.number="options.image.width" :label="$t('Global.Common.width')" type="number" hint="pixels"></v-text-field>
            </v-col>
          </template>
          <template v-if="selectedMode === 'pdf'">
            <v-col cols="4">
              <v-select v-model="options.pdf.documentSize" :items="pdfSizes" :label="$t('Qlik.Exporter.format')"></v-select>
            </v-col>
            <v-col cols="4">
              <v-select v-model="options.pdf.orientation" :items="pdfOrientations" :label="$t('Qlik.Exporter.orientation')"></v-select>
            </v-col>
            <v-col cols="4">
              <v-select v-model="options.pdf.aspectRatio" :items="pdfRatio" :label="$t('Qlik.Exporter.ratio')"></v-select>
            </v-col>
          </template>
          <v-col cols="12">
            <v-alert type="success" transition="scale-transition" border="right" prominent :value="url && !loading">
              <v-row align="center">
                <v-col class="grow">
                  {{ $t('Qlik.Exporter.success') }}
                </v-col>
                <v-col class="shrink">
                  <v-btn light :href="url">{{ $t('Global.Action.download') }}</v-btn>
                </v-col>
              </v-row>
            </v-alert>
            <v-alert type="error" transition="scale-transition" border="right" prominent :value="error && !loading">
              <v-row align="center">
                <v-col class="grow">
                  {{ $t('Qlik.Exporter.error') }}
                </v-col>
                <v-col class="shrink">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                      >
                        info
                      </v-icon>
                    </template>
                    <span>{{ $t('Qlik.Exporter.errorInfo') }}</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn block color="primary" @click="startExport" :loading="loading">
          {{ $t('Global.Action.export') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { exportData, exportImg, exportPdf } from '@portal/qlik'
import { mapMutations, mapState } from 'vuex'
export default {
  name: 'QlikExporter',
  data: () => ({
    selectedMode: 'csv',
    url: null,
    loading: false,
    error: null,
    options: null,
  }),
  computed: {
    ...mapState('qlik', [
      'exporter',
    ]),
    modes () {
      return [
        { name: 'csv', icon: 'format_list_bulleted' },
        { name: 'image', icon: 'image' },
        { name: 'pdf', icon: 'picture_as_pdf' },
      ]
    },
    csvTypes () {
      return [
        { text: this.$t('Qlik.Exporter.ooxml'), value: 'OOXML' },
        { text: this.$t('Qlik.Exporter.csv_c'), value: 'CSV_C' },
        { text: this.$t('Qlik.Exporter.csv_t'), value: 'CSV_T' }
      ]
    },
    csvStates () {
      return [
        { text: this.$t('Qlik.Exporter.state_p'), value: 'P' },
        { text: this.$t('Qlik.Exporter.state_a'), value: 'A' }
      ]
    },
    pdfOrientations () {
      return [
        { text: this.$t('Qlik.Exporter.landscape'), value: 'landscape' },
        { text: this.$t('Qlik.Exporter.portrait'), value: 'portrait' }
      ]
    },
    pdfSizes () {
      return [
        { text: 'A3', value: 'a3' },
        { text: 'A4', value: 'a4' },
        { text: 'A5', value: 'a5' }
      ]
    },
    pdfRatio () {
      return [
        { text: this.$t('Qlik.Exporter.currentSize'), value: 0 },
        { text: this.$t('Qlik.Exporter.fitToPage'), value: 2 }
      ]
    }
  },
  watch: {
    selectedMode() {
      this.url = null
      this.error = null
    },
    exporter(value) {
      if (value) {
        const el = document.getElementById(value.qObjectId)
        this.options.image.height = el.offsetHeight
        this.options.image.width = el.offsetWidth
        this.options.pdf.objectSize = {
          height: el.offsetHeight,
          width: el.offsetWidth
        }
      }
    }
  },
  created() {
    this.init()
  },
  methods: {
    ...mapMutations('qlik', [
      'closeExporter'
    ]),
    init () {
      this.selectedMode = 'csv'
      this.url = null
      this.error = null
      this.loading = false
      this.options = {
        csv: {
          qFileType: 'OOXML',
          qExportState: 'A'
        },
        image: {
          imageType: 'png', 
          height: 600,
          width: 800
        },
        pdf: {
          documentSize: 'a4',
          aspectRatio: 2,
          orientation: 'landscape',
        }
      }
    },
    async startExport () {
      this.error = false
      this.loading = true
      try {
        switch (this.selectedMode) {
          case 'csv':
            this.url = await exportData(this.$qlik[this.exporter.qAppId].enigmaDoc, this.exporter.qObjectId, this.options.csv)
            break;
          case 'image':
            this.url = await exportImg(this.$qlik[this.exporter.qAppId].capabilityApp, this.exporter.qObjectId, this.options.image)
            break;
          case 'pdf':
            this.url = await exportPdf(this.$qlik[this.exporter.qAppId].capabilityApp, this.exporter.qObjectId, this.options.pdf)
            break;
          default:
            this.url = null
            break;
        }
      } catch (error) {
        this.url = null
        this.error = error
      }
      
      this.loading = false
    },
    close () {
      this.closeExporter()
      this.init()
    }
  }
}
</script>